import { useState } from 'react';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Equipment } from '@models/equipment.model';
import { graphQlClient } from '@config/graphqlClient';
import EquipmentGraphQL from '@graphql/equipment.queries';

interface EditEquipmentParams {
  equipmentId: string;
  name: string;
  supplierOwner: string;
  time: string;
  hoursUsed: number;
  amount: number;
  costPerHour: number;
  pricePerHour?: number;
  taskId: string;
  chargeToClient: boolean;
}

interface UpdateEquipmentResponse {
  updateEquipment: Equipment;
}

interface UseEditEquipmentReturn {
  editEquipment: (params: EditEquipmentParams) => Promise<Equipment | null>;
  loading: boolean;
}

export const useEditEquipment = (): UseEditEquipmentReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');

  const editEquipment = async (
    params: EditEquipmentParams,
  ): Promise<Equipment | null> => {
    setLoading(true);

    try {
      const equipmentData = {
        equipmentId: params.equipmentId,
        name: params.name,
        supplierOwner: params.supplierOwner,
        time: params.time,
        hoursUsed: params.hoursUsed,
        amount: params.amount,
        costPerHour: params.costPerHour,
        pricePerHour: params.pricePerHour || params.costPerHour,
        taskId: params.taskId,
        chargeToClient: params.chargeToClient,
      };

      const data: UpdateEquipmentResponse = await graphQlClient.request(
        EquipmentGraphQL.mutations.updateEquipment,
        {
          equipment: equipmentData,
        },
      );

      if (data?.updateEquipment) {
        dispatch(
          appendActionMessage({
            message: localeCommon['updatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );

        return data.updateEquipment;
      }

      return null;
    } catch (error: any) {
      console.error('useEditEquipment - error:', error);
      console.error('useEditEquipment - error response:', error?.response);

      dispatch(
        appendActionMessage({
          message:
            error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { editEquipment, loading };
};
